import { useState } from 'react';
import type { ReactNode } from 'react';
import { Button } from '../../components/ui';
import { displayDealRows, moreLabel, pipelineLabel } from './briefDeals';
import type { BriefDealRow, BriefDealsPayload, BriefPipeline, BriefWatchRow } from './briefDeals';

function ToggleRow({ label, children }: { label: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="brief-deal">
      <button
        type="button"
        className="brief-deal-toggle"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        {label}
      </button>
      {open ? <div className="brief-deal-detail">{children}</div> : null}
    </li>
  );
}
function RedRow({ row }: { row: BriefDealRow }) {
  return (
    <ToggleRow label={row.name}>
      <p>{row.why}</p>
      {row.deadline ? <p>Deadline: {row.deadline}</p> : null}
      {row.last_touch_on ? (
        <p>
          Last touch: {row.last_touch_on}
          {row.last_touch_kind ? ` · ${row.last_touch_kind}` : ''}
        </p>
      ) : null}
      {row.next_line ? <p>Next: {row.next_line}</p> : null}
      {row.owner_first_name ? <p>Owner: {row.owner_first_name}</p> : null}
    </ToggleRow>
  );
}
function WatchRow({ row }: { row: BriefWatchRow }) {
  return (
    <ToggleRow label={row.name}>
      <p>{row.why}</p>
    </ToggleRow>
  );
}
function PipelineSection({
  pipeline,
  data,
}: {
  pipeline: BriefPipeline;
  data: BriefDealsPayload[BriefPipeline];
}) {
  return (
    <section className="brief-deals-pipeline" aria-label={pipelineLabel(pipeline)}>
      <h2 className="brief-deals-title">{pipelineLabel(pipeline)}</h2>
      <div className="brief-deal-counts">
        <span>Act today {data.counts.red}</span>
        <span>Watch {data.counts.yellow}</span>
        <span>Steady {data.counts.green}</span>
      </div>
      {data.reds.length ? (
        <ol className="brief-deals-list">
          {displayDealRows(data.reds).map((row) => (
            <RedRow key={row.id} row={row} />
          ))}
        </ol>
      ) : null}
      {data.watch.length ? (
        <>
          <h3>Watch ({data.watch.length})</h3>
          <ol className="brief-deals-list">
            {data.watch.map((row) => (
              <WatchRow key={row.id} row={row} />
            ))}
          </ol>
        </>
      ) : null}
      {pipeline === 'cg_deals' && data.orders_at_risk && data.orders_at_risk.length ? (
        <>
          <h3>Orders at risk ({data.orders_at_risk.length})</h3>
          <ol className="brief-deals-list">
            {data.orders_at_risk.map((order) => (
              <ToggleRow key={order.order_no} label={order.order_no}>
                <p>{order.client}</p>
                <p>Target ship date: {order.target_ship_date}</p>
              </ToggleRow>
            ))}
          </ol>
        </>
      ) : null}
      {moreLabel(data.red_more) ? (
        <p className="brief-deals-more">{moreLabel(data.red_more)}</p>
      ) : null}
    </section>
  );
}
export function BriefDealsSection({
  deals,
  variant,
}: {
  deals: BriefDealsPayload;
  variant: 'hub' | 'portal';
}) {
  return (
    <section className="brief-deals" aria-label="Brief deals">
      <div className="brief-deals-pipelines">
        <PipelineSection pipeline="partnerships" data={deals.partnerships} />
        <PipelineSection pipeline="cg_deals" data={deals.cg_deals} />
      </div>
      {variant === 'hub' ? (
        <Button
          className="brief-deals-board"
          variant="secondary"
          size="sm"
          onClick={() => {
            window.location.assign('/deals');
          }}
        >
          Open the full deal board
        </Button>
      ) : null}
    </section>
  );
}
